import * as React from 'react';
import { useSelector } from 'react-redux';
import { Tabs } from '@cian/ui-kit/prev';
import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';

import { trackMortgageOffersTabClick, trackPromoOffersShow } from '../../analytics/promos';
import { MortgageSection } from '../../components/MortgageSection';

import { AccreditedBanksContainer } from '../AccreditedBanks';
import { MortgageOffersContainer } from '../MortgageOffers';

import { getInitialTab, getSectionTabs, getSectionTitle } from './helpers';
import { EMortgageOffersTabs } from '../../types/mortgage';
import { IPromoInfo } from '../../types/newbuilding/promoInfo';
import { hasBanksSelector, hasMortgagePromosSelector, newbuildingIdSelector } from '../../selectors';

export const MortgageSectionContainer: React.FC = () => {
  const hasMortgageOffers = useSelector(hasMortgagePromosSelector);
  const hasBanks = useSelector(hasBanksSelector);
  const newbuildingId = useSelector(newbuildingIdSelector);

  const { tabs, title } = React.useMemo(
    () => ({
      tabs: getSectionTabs({ hasBanks, hasMortgageOffers }),
      title: getSectionTitle({ hasBanks, hasMortgageOffers }),
    }),
    [hasBanks, hasMortgageOffers],
  );

  const [activeTab, setActiveTab] = React.useState(getInitialTab(tabs));

  const displayedOffersRef = React.useRef<IPromoInfo[]>([]);

  const handleDisplayedOffersChange = React.useCallback((displayedOffers: IPromoInfo[]) => {
    displayedOffersRef.current = displayedOffers;
  }, []);

  const handleShow = React.useCallback(() => {
    trackPromoOffersShow(displayedOffersRef.current, newbuildingId, true);
  }, [newbuildingId]);

  const handleTabChange = React.useCallback((tab: EMortgageOffersTabs) => {
    setActiveTab(tab);
    trackMortgageOffersTabClick(tab);
  }, []);

  const hasTabs = tabs.length > 0;
  const isTabsNodeVisible = tabs.length > 1;

  if (!hasTabs) {
    return null;
  }

  return (
    <ActionAfterViewObserver callback={handleShow} triggerOnce>
      <MortgageSection
        title={title}
        tabs={isTabsNodeVisible && <Tabs activeTab={activeTab} onChange={handleTabChange} tabs={tabs} />}
      >
        {activeTab === EMortgageOffersTabs.Mortgage && (
          <MortgageOffersContainer onDisplayedOffersChange={handleDisplayedOffersChange} />
        )}
        {activeTab === EMortgageOffersTabs.Banks && <AccreditedBanksContainer />}
      </MortgageSection>
    </ActionAfterViewObserver>
  );
};
